// apps/web/src/widgets/shopping-list-aggregator/renderer.tsx
//
// Phase 11.8 — REND-01 (Strand 1) ShoppingListAggregatorRenderer. The sole
// remaining cataloged-but-unregistered template; this is its renderer (the
// ADD-A-TEMPLATE step-4 surface). It is PURE-DISPLAY: StateForTemplate is
// `undefined` (no persisted WidgetState, no 3-site lockstep). It reads TWO
// sibling widget states (an option-set `{ selected }` + a scaling-matrix
// `{ axisSelections, multiplier }`) via ONE top-level useContext, resolves the
// selected sizing pass positionally, sums each line across the selected options
// (the reference per-pass model), groups the result by the recipe-declared
// bucket, and routes EVERY displayed amount through the Plan-03 formatter.
//
// Multi-source read (Rules of Hooks — the summary-checklist:70-73 precedent):
// ONE top-level useContext(RecipeStateContext); the two sources are read with
// ctx.states.get(config.aggregateFrom[i]). NEVER a hook inside a .map.
//
// Positional sizing resolution (the 11.6 precedent — branch-variant/
// format-override-value.ts resolveSizeIndex): the scaling-matrix state holds
// STEP-ID strings, not indices. The scale = (selected size step multiplier) ×
// (the typed multiplier). A no-selection / stale-id drift degrades to scale 1.
//
// Collapse is EPHEMERAL (ONE useState<boolean>) — the reference single-panel
// toggle. NO persisted state, NO schema touch.
//
// C1 generality (scripts/grep-no-recipe-identifiers.sh): the renderer names NO
// unit, NO category, and hardcodes NO recipe vocabulary. Bucket labels resolve
// via the REUSED resolveGroupLabel (imported from summary-checklist, not
// re-derived); amounts via the units-opaque formatAmount; the grouping branch
// keys on the only non-domain literal "none". No shopping-bucket field is added
// to the Ingredient shape — buckets derive from the existing ingredient-group
// membership. Every displayed string flows from config / recipe DATA.
//
// XSS discipline (T-11.8-XSS): every DATA string is a JSX text child (React
// auto-escapes); the formatAmount output is a plain string consumed as text. No
// dangerouslySetInnerHTML. Mirrors the text-child precedent.
import type { MeasurementVocabulary, RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useContext, useId, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { resolveSizeIndex } from "../branch-variant/format-override-value.js";
import { formatAmount } from "../measurement/format-amount.js";
import { RecipeStateContext } from "../recipe-state-provider.js";
import { resolveGroupLabel } from "../summary-checklist/group-label.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { type AggregateGroupInput, aggregate, sumBucket } from "./aggregate.js";

// Generic marker for the single flat bucket — resolveGroupLabel maps "_flat" to
// "" (an empty section label). Not a domain term.
const FLAT_BUCKET_KEY = "_flat";
// The U+2014 mark the renderer shows when the formatter returns undefined (its
// degenerate-input fallback — mirrors format-yield's "Yield: —").
const EMDASH = "—";

// A scaling-matrix-shaped state (the second source). Read structurally — the
// pure-display aggregator never narrows the WidgetState union itself.
interface ScalerStateShape {
  readonly axisSelections: Readonly<Record<string, string>>;
  readonly multiplier: number;
}

function isScalerState(value: unknown): value is ScalerStateShape {
  return (
    typeof value === "object" &&
    value !== null &&
    "axisSelections" in value &&
    "multiplier" in value
  );
}

function isSelectionState(value: unknown): value is { readonly selected: ReadonlyArray<string> } {
  return typeof value === "object" && value !== null && "selected" in value;
}

// Shared Card-shell + heading fallback for the graceful-degrade branches
// (mirrors per-option-detail-card/ranked-tier FallbackShell discipline).
function FallbackShell({
  labelId,
  heading,
  message,
}: {
  labelId: string;
  heading: string;
  message: string;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {heading}
          </h3>
          <p className="text-sm text-[var(--recipe-muted)]">{message}</p>
        </section>
      </CardContent>
    </Card>
  );
}

// Render one amount via the units-opaque formatter. The primary is ink; each
// material alternate is an inline .v-note. An undefined formatter return (or an
// absent vocabulary) → the U+2014 fallback. The renderer NEVER formats amounts
// itself.
function AmountText({
  amount,
  vocab,
}: {
  amount: number | undefined;
  vocab: MeasurementVocabulary | undefined;
}): React.ReactElement {
  const formatted =
    amount !== undefined && vocab !== undefined ? formatAmount(amount, vocab) : undefined;
  if (formatted === undefined) {
    return <span className="text-sm text-[var(--recipe-ink)]">{EMDASH}</span>;
  }
  return (
    <span className="text-sm text-[var(--recipe-ink)]">
      {formatted.primary}
      {formatted.alts.map((alt) => (
        <span key={alt} className="ml-1 text-xs text-[var(--recipe-muted)]">
          {alt}
        </span>
      ))}
    </span>
  );
}

export function ShoppingListAggregatorRenderer({
  widget,
  config,
  recipe,
}: WidgetRendererProps<"shopping-list-aggregator">): React.ReactElement {
  // ONE top-level context read (Rules of Hooks) — both sources come from it.
  const ctx = useContext(RecipeStateContext);
  if (ctx === null) {
    throw new Error(
      "<ShoppingListAggregatorRenderer> must be rendered inside <RecipeStateProvider>",
    );
  }

  // Ephemeral whole-list collapse — ONE boolean, default expanded. No persistence.
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const reactId = useId();
  const labelId = `${widget.instanceId}-label`;
  const bodyId = `${widget.instanceId}-body`;

  // Resolve the two sources by id. config.aggregateFrom[0] = the selection
  // source (option-set {selected}); [1] = the sizing source (scaling-matrix).
  const selectionId = config.aggregateFrom[0];
  const sizingId = config.aggregateFrom[1];
  const selectionState = selectionId !== undefined ? ctx.states.get(selectionId) : undefined;
  const sizingState = sizingId !== undefined ? ctx.states.get(sizingId) : undefined;

  // (1) Missing/malformed FIRST source → "No sources to aggregate." The first
  // source drives pass selection; without a valid one there is nothing to
  // aggregate. (The second source is optional — absent → scale 1.)
  if (
    selectionId === undefined ||
    (selectionState !== undefined && !isSelectionState(selectionState))
  ) {
    return (
      <FallbackShell labelId={labelId} heading={widget.name} message="No sources to aggregate." />
    );
  }

  const selectedIds: ReadonlyArray<string> = isSelectionState(selectionState)
    ? Array.from(new Set(selectionState.selected))
    : [];

  // Resolve the per-pass scale from the sizing source (positional size × typed
  // multiplier). A no-selection / drift / non-finite path → a neutral scale.
  const scale = resolveScale(recipe, sizingId, sizingState);

  // Build the recipe's grouped ingredient lines (bucket key = the group label as
  // declared — the renderer resolves it to a display label via resolveGroupLabel).
  const groups: ReadonlyArray<AggregateGroupInput> = (recipe.ingredientGroups ?? []).map((g) => ({
    bucketKey: g.label,
    lines: g.items.map((item) => ({
      name: item.name,
      amount: item.quantity,
      note: item.note,
    })),
  }));

  const flat = config.grouping === "none";
  const buckets = aggregate({
    groups,
    passCount: selectedIds.length,
    scale,
    flat,
    flatBucketKey: FLAT_BUCKET_KEY,
  });

  const vocab: MeasurementVocabulary | undefined = recipe.measurementVocabulary;
  const footnotes = config.footnotes ?? [];
  const totals = config.totals ?? [];

  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-3">
          {/* Whole-list collapse — disclosure-inside-heading (11.7 idiom). */}
          <h4 className="contents">
            <button
              type="button"
              aria-expanded={!collapsed}
              aria-controls={bodyId}
              onClick={() => setCollapsed((c) => !c)}
              className="flex w-full items-center gap-1 text-left"
            >
              {collapsed ? (
                <ChevronRight size={14} aria-hidden />
              ) : (
                <ChevronDown size={14} aria-hidden />
              )}
              <span
                id={labelId}
                className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
              >
                {widget.name}
              </span>
            </button>
          </h4>

          {/* aria-controls target stays in the DOM; hidden when collapsed. */}
          <div id={bodyId} hidden={collapsed} className="flex flex-col gap-3">
            {selectedIds.length === 0 ? (
              <p className="text-sm text-[var(--recipe-muted)]">
                Nothing selected yet — pick options to build the list.
              </p>
            ) : null}

            {/* Per-group subsections (one per recipe-declared bucket). */}
            {buckets.map((bucket) => {
              const groupLabel = resolveGroupLabel(bucket.bucketKey, recipe.optionCategories);
              const groupLabelId = `${reactId}-group-${bucket.bucketKey}`;
              return (
                <section
                  key={bucket.bucketKey}
                  aria-labelledby={groupLabelId}
                  className="flex flex-col gap-1"
                >
                  {groupLabel !== "" ? (
                    <h4
                      id={groupLabelId}
                      className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
                    >
                      {groupLabel}
                    </h4>
                  ) : null}
                  <ul className="flex flex-col gap-1">
                    {bucket.lines.map((line) => (
                      <li
                        key={line.name}
                        className="flex flex-wrap items-baseline gap-2 text-sm text-[var(--recipe-ink)]"
                      >
                        <AmountText amount={line.amount} vocab={vocab} />
                        <span>{line.name}</span>
                        {line.note !== undefined ? (
                          <span className="text-xs text-[var(--recipe-muted)]">{line.note}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {/* Footnotes block — omitted entirely when absent. */}
            {footnotes.length > 0 ? (
              <section
                aria-labelledby={`${reactId}-footnotes`}
                className="flex flex-col gap-1 border-t border-[var(--recipe-line)] pt-2"
              >
                <h4
                  id={`${reactId}-footnotes`}
                  className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
                >
                  Notes
                </h4>
                <ul className="flex flex-col gap-1">
                  {footnotes.map((footnote) => (
                    <li key={footnote.id} className="text-xs text-[var(--recipe-muted)]">
                      {footnote.label}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Totals block — omitted entirely when absent. */}
            {totals.length > 0 ? (
              <section
                aria-labelledby={`${reactId}-totals`}
                className="flex flex-col gap-1 border-t border-[var(--recipe-line)] pt-2"
              >
                <h4
                  id={`${reactId}-totals`}
                  className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
                >
                  Totals
                </h4>
                <ul className="flex flex-col gap-1">
                  {totals.map((totalsLabel) => (
                    <li
                      key={totalsLabel}
                      className="flex flex-wrap items-baseline gap-1 text-sm text-[var(--recipe-ink)]"
                    >
                      <span>{totalsLabel}:</span>
                      <AmountText amount={sumBucket(buckets, totalsLabel)} vocab={vocab} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </section>
      </CardContent>
    </Card>
  );
}

// Resolve the per-pass scale from the sizing (scaling-matrix) source. Find the
// scaling-matrix widget by id, take its FIRST axis (the size axis), resolve the
// selected step positionally (resolveSizeIndex), read that step's multiplier, and
// multiply by the typed multiplier. Any miss / non-finite path → a neutral scale
// (the typed multiplier alone when finite, else 1). The math operates on recipe
// DATA; no unit/category literal.
function resolveScale(
  recipe: RecipeDefinitionV1,
  sizingId: string | undefined,
  sizingState: unknown,
): number {
  if (sizingId === undefined || !isScalerState(sizingState)) return 1;
  const widgets = recipe.widgets ?? [];
  const sizingWidget = widgets.find((w) => w.instanceId === sizingId);
  if (sizingWidget === undefined) return 1;
  const axes = (
    sizingWidget.config as {
      axes?: ReadonlyArray<{
        id: string;
        steps: ReadonlyArray<{ id: string; multiplier: number }>;
      }>;
    }
  ).axes;
  const sizeAxis = axes?.[0];
  if (sizeAxis === undefined) {
    return Number.isFinite(sizingState.multiplier) && sizingState.multiplier > 0
      ? sizingState.multiplier
      : 1;
  }
  const selectedStepId = sizingState.axisSelections[sizeAxis.id];
  const idx = resolveSizeIndex(sizeAxis.steps, selectedStepId);
  const step = idx >= 0 ? sizeAxis.steps[idx] : undefined;
  const stepMultiplier = step?.multiplier;
  const typedMultiplier = sizingState.multiplier;
  if (
    stepMultiplier === undefined ||
    !Number.isFinite(stepMultiplier) ||
    !Number.isFinite(typedMultiplier)
  ) {
    // No size selected (or drift) → the typed multiplier alone when finite/positive.
    return Number.isFinite(typedMultiplier) && typedMultiplier > 0 ? typedMultiplier : 1;
  }
  const product = stepMultiplier * typedMultiplier;
  return Number.isFinite(product) && product > 0 ? product : 1;
}
